import { FastbootError } from "./errors";

/**
 * Minimal byte transport the fastboot client talks over. One bulk OUT + one bulk IN
 * endpoint. Implemented by WebUSB in the browser and by FakeUsbDevice in tests.
 */
export interface FastbootTransport {
  /** USB serial string if known (`serialno` from getvar is authoritative). */
  readonly serial: string | undefined;
  open(): Promise<void>;
  close(): Promise<void>;
  send(data: Uint8Array): Promise<void>;
  /** One bulk IN transfer, returns whatever the device sent (≤ maxLen bytes). */
  receive(maxLen: number): Promise<Uint8Array>;
}

/** USB interface triple that identifies an Android fastboot interface. */
export const FASTBOOT_USB_CLASS = 0xff;
export const FASTBOOT_USB_SUBCLASS = 0x42;
export const FASTBOOT_USB_PROTOCOL = 0x03;

export const FASTBOOT_USB_FILTER: USBDeviceFilter = {
  classCode: FASTBOOT_USB_CLASS,
  subclassCode: FASTBOOT_USB_SUBCLASS,
  protocolCode: FASTBOOT_USB_PROTOCOL,
};

/** Google's USB vendor id — used only to pre-filter the chooser, not to trust a device. */
export const GOOGLE_USB_VID = 0x18d1;

interface Located {
  configuration: number;
  iface: number;
  alternate: number;
  epIn: number;
  epOut: number;
  packetSizeOut: number;
}

function locateFastbootInterface(device: USBDevice): Located | undefined {
  for (const cfg of device.configurations) {
    for (const iface of cfg.interfaces) {
      for (const alt of iface.alternates) {
        if (
          alt.interfaceClass !== FASTBOOT_USB_CLASS ||
          alt.interfaceSubclass !== FASTBOOT_USB_SUBCLASS ||
          alt.interfaceProtocol !== FASTBOOT_USB_PROTOCOL
        ) {
          continue;
        }
        const epIn = alt.endpoints.find((e) => e.direction === "in" && e.type === "bulk");
        const epOut = alt.endpoints.find((e) => e.direction === "out" && e.type === "bulk");
        if (epIn && epOut) {
          return {
            configuration: cfg.configurationValue,
            iface: iface.interfaceNumber,
            alternate: alt.alternateSetting,
            epIn: epIn.endpointNumber,
            epOut: epOut.endpointNumber,
            packetSizeOut: epOut.packetSize,
          };
        }
      }
    }
  }
  return undefined;
}

/** WebUSB implementation. Construct with a device obtained from `requestFastbootDevice()`. */
export class WebUsbTransport implements FastbootTransport {
  private located: Located | undefined;

  constructor(private readonly device: USBDevice) {}

  get serial(): string | undefined {
    return this.device.serialNumber ?? undefined;
  }

  async open(): Promise<void> {
    const loc = locateFastbootInterface(this.device);
    if (!loc) {
      throw new FastbootError("no_interface", "USB device has no fastboot interface (is the phone in fastboot mode?)");
    }
    this.located = loc;
    await this.device.open();
    if (this.device.configuration?.configurationValue !== loc.configuration) {
      await this.device.selectConfiguration(loc.configuration);
    }
    await this.device.claimInterface(loc.iface);
    if (loc.alternate !== 0) await this.device.selectAlternateInterface(loc.iface, loc.alternate);
  }

  async close(): Promise<void> {
    try {
      if (this.located) await this.device.releaseInterface(this.located.iface);
    } catch {
      /* device may already be gone */
    }
    try {
      await this.device.close();
    } catch {
      /* ignore */
    }
  }

  async send(data: Uint8Array): Promise<void> {
    const loc = this.mustBeOpen();
    const view = data.buffer instanceof ArrayBuffer ? data : new Uint8Array(data);
    const res = await this.device.transferOut(loc.epOut, view as BufferSource);
    if (res.status !== "ok") throw new FastbootError("disconnected", `USB transferOut ${res.status ?? "failed"}`);
    if ((res.bytesWritten ?? 0) !== data.byteLength) {
      throw new FastbootError("protocol", `short USB write: ${res.bytesWritten ?? 0}/${data.byteLength}`);
    }
  }

  async receive(maxLen: number): Promise<Uint8Array> {
    const loc = this.mustBeOpen();
    const res = await this.device.transferIn(loc.epIn, maxLen);
    if (res.status === "stall") {
      await this.device.clearHalt("in", loc.epIn);
      throw new FastbootError("protocol", "USB IN endpoint stalled");
    }
    if (res.status !== "ok" || !res.data) throw new FastbootError("disconnected", `USB transferIn ${res.status ?? "failed"}`);
    return new Uint8Array(res.data.buffer, res.data.byteOffset, res.data.byteLength);
  }

  private mustBeOpen(): Located {
    if (!this.located) throw new FastbootError("protocol", "transport not open");
    return this.located;
  }
}

export function webUsbAvailable(nav: Navigator = navigator): boolean {
  return typeof nav !== "undefined" && "usb" in nav && typeof nav.usb?.requestDevice === "function";
}

/** Must be called from a user gesture (click). Shows the browser's device chooser. */
export async function requestFastbootDevice(nav: Navigator = navigator): Promise<USBDevice> {
  if (!webUsbAvailable(nav)) throw new FastbootError("unsupported", "WebUSB is not available in this browser");
  return nav.usb.requestDevice({ filters: [FASTBOOT_USB_FILTER] });
}

/** Devices the user already granted access to (survives reload). */
export async function grantedFastbootDevices(nav: Navigator = navigator): Promise<USBDevice[]> {
  if (!webUsbAvailable(nav)) return [];
  const all = await nav.usb.getDevices();
  return all.filter((d) => locateFastbootInterface(d) !== undefined);
}
